import {Switch} from '@/components/commons/switch/Switch'
import dayjs, {formatDate} from '@/dayjs'
import {AgendaEventFieldsSchema} from '@/features/agenda/formSchemas'
import {getWeekPositionInMonth} from '@/features/agenda/utils'
import {DayOfWeek} from '@/features/users-availability/types'
import {useTranslation} from '@/translations/i18n'
import {useController, useFormContext, useWatch} from 'react-hook-form'
import {z} from 'zod'

const AgendaRepeatScheduleSwitchFieldSchema = AgendaEventFieldsSchema.pick({
    date: true,
    isRepeated: true,
    repetitionData: true,
    consultants: true
})
type AgendaRepeatScheduleSwitchFieldSchema = z.infer<typeof AgendaRepeatScheduleSwitchFieldSchema>

export const AgendaRepeatScheduleSwitchField = () => {
    const {t} = useTranslation()
    const {control, resetField} = useFormContext<AgendaRepeatScheduleSwitchFieldSchema>()
    const date = useWatch({control, name: 'date'})
    const {field} = useController({control, name: 'isRepeated'})
    const {field: repetitionDataField} = useController({control, name: 'repetitionData'})
    const consultants = useWatch({control, name: 'consultants'})
    const plannedAccessTreatmentFrequency = consultants?.[0]?.accessType?.frequency

    if (!date) return null

    const handleChangeSwitch = (value: boolean) => {
        field.onChange(value)
        if (!value) {
            resetField('repetitionData')
        } else {
            if (plannedAccessTreatmentFrequency == 'weekly') {
                repetitionDataField.onChange({
                    frequency: 'weekly',
                    position: 1,
                    dayOfWeek: formatDate(date.date, 'dddd').toLowerCase() as DayOfWeek
                })
            }

            if (plannedAccessTreatmentFrequency == 'monthly') {
                repetitionDataField.onChange({
                    frequency: 'monthly',
                    position: getWeekPositionInMonth(dayjs(date.date).date()),
                    dayOfWeek: formatDate(date.date, 'dddd').toLowerCase() as DayOfWeek
                })
            }
        }
    }

    return (
        <Switch
            size="sm"
            label={t('agenda:repeat')}
            checked={field.value}
            onChange={handleChangeSwitch}
            disabled={!date || !date.date || !date.time || !date.location}
        />
    )
}
